"use client";

import { useState } from "react";
import type { ResponseMode } from "@/lib/ai/responseModes";
import { RESPONSE_MODES, getAvailableModes, detectBestMode } from "@/lib/ai/responseModes";
import type { SubscriptionPlan } from "@/lib/subscription/plans";
import { getUserPlan } from "@/lib/subscription/check";
import type { AiProfile } from "@/types/ai";
import { Zap, Settings, Sparkles, GraduationCap, Target, Search, Lock, Wand2 } from "lucide-react";

interface Props {
  profile: AiProfile;
  currentMode: ResponseMode;
  onModeChange: (mode: ResponseMode) => void;
}

/**
 * Response Mode Selector
 * 
 * Shows available modes based on subscription tier.
 * Free users: Quick only (locked)
 * Premium users: All modes unlocked
 */
export default function ResponseModeSelector({ profile, currentMode, onModeChange }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const plan = getUserPlan(profile);
  const availableModes = getAvailableModes(plan);
  const currentConfig = RESPONSE_MODES[currentMode];

  const getModeIcon = (mode: ResponseMode) => {
    const config = RESPONSE_MODES[mode];
    switch (config.icon) {
      case "🎯": return <Wand2 className="w-4 h-4" />;
      case "⚡": return <Zap className="w-4 h-4" />;
      case "⚙️": return <Settings className="w-4 h-4" />;
      case "✨": return <Sparkles className="w-4 h-4" />;
      case "🎓": return <GraduationCap className="w-4 h-4" />;
      case "🔍": return <Search className="w-4 h-4" />;
      default: return <Zap className="w-4 h-4" />;
    }
  };

  return (
    <>
      {/* Current Mode Button - Compact trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-gray-300/80 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-800 shadow-md transition-all hover:shadow-lg text-xs"
        title={`Response Mode: ${currentConfig.name}`}
      >
        {getModeIcon(currentMode)}
        <span className="text-xs font-semibold text-gray-900 dark:text-gray-100 hidden sm:inline [text-shadow:0_0_1px_rgba(255,255,255,0.8)] dark:[text-shadow:0_0_1px_rgba(0,0,0,0.3)]">
          {currentConfig.name}
        </span>
        {plan === "free" && currentMode !== "quick" && (
          <Lock className="w-3 h-3 text-yellow-500" />
        )}
      </button>

      {/* Mode Modal/Popover - Discrete, doesn't cover large sections */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 w-[85vw] max-w-xs bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200">
            <div className="p-2">
              {Object.values(RESPONSE_MODES).map((config) => {
                const isAvailable = availableModes.includes(config.mode);
                const isSelected = currentMode === config.mode;
                const isLocked = !isAvailable;

                return (
                  <button
                    key={config.mode}
                    onClick={() => {
                      if (isAvailable) {
                        onModeChange(config.mode);
                        setIsOpen(false);
                      }
                    }}
                    disabled={isLocked}
                    className={`w-full text-left p-3 rounded-lg transition-all ${
                      isSelected
                        ? "bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500"
                        : isLocked
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`mt-0.5 ${isLocked ? "text-gray-400" : "text-gray-700 dark:text-gray-300"}`}>
                          {getModeIcon(config.mode)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${isLocked ? "text-gray-400" : "text-gray-900 dark:text-gray-100"}`}>
                              {config.name}
                            </span>
                            {config.requiresPremium && isLocked && (
                              <Lock className="w-3 h-3 text-yellow-500" />
                            )}
                            {config.mode === "smart" && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                New
                              </span>
                            )}
                            {config.mode === "study" && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                New
                              </span>
                            )}
                            {config.mode === "search" && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                New
                              </span>
                            )}
                          </div>
                          <p className={`text-xs mt-1 ${isLocked ? "text-gray-400" : "text-gray-600 dark:text-gray-400"}`}>
                            {config.description}
                          </p>
                          {config.timeEstimate && (
                            <p className="text-xs mt-1 text-gray-500 dark:text-gray-500">
                              {config.timeEstimate}
                            </p>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            
            {/* Upgrade Prompt for Free Users */}
            {plan === "free" && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200 dark:border-yellow-800">
                <p className="text-xs text-yellow-800 dark:text-yellow-200">
                  Upgrade to Pro to unlock all response modes
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}

